import type { SheetModel, SheetUuid } from "@tento-lona/sheets";
import type { Option } from "@tento-lona/sheets";
import { ColumnIndex } from "@tento-core/column-index";
import type { RowLocalId } from "@tento-lona/sheets";
import type { SheetRow } from "../rows/sheet-row.ts";
import type { SheetRowReads } from "../rows/sheet-row-reads.ts";
import { RowKey, SheetId } from "@tento-lona/sheets";
import { platformOf, LonaSdk, SheetHandle } from "../client/client.ts";
import { sheetChromeOf } from "../internal/doors.ts";
import type { Actor, InvalidationSink } from "../invalidation.ts";
import type {
  ModelRowReads,
} from "@tento-lona/sheets";
import type { Rational } from "@tento-core/rational";
import type { SheetRowTree } from "../rows/sheet-tree.ts";
import { SheetLayoutWidth } from "@tento-core/sheet-layout/sheet-layout-width";
import type { SheetLayoutSpec } from "@tento-core/sheet-layout/sheet-layout-spec";
import { Tracing } from "@tento-core/tracing";
import { NaiveDate } from "@tento-chrono";
import { Network } from "../network.ts";
import { SystemRows, SystemRow, isBuiltinSheetKey } from "@tento-lona/sheets";
import { SheetViewConfig } from "@tento-lona/sheets";
import type { Timescale } from "./view-model.ts";

/**
 * The reactive model behind a rendered sheet: the handle it shows, its view
 * configuration, column index and anchor date. `Binder` is what its
 * renderer binds a column with (`SheetViewModel.Renderer.toBinder`): the
 * sheets-ui `SheetBinder` for a DOM view, `never` for a headless one.
 */
export class SheetViewModel<Binder> {
  /** The client + renderer factory this view was made with; carried by `clone`. */
  readonly #deps: SheetViewModel.Deps<Binder>;
  constructor(
    readonly indexRenderer: SheetViewModel.Renderer<Binder>,
    readonly viewConfig: SheetViewConfig,
    /** Whether the view config states the column count (`"explicit"`) or the width derives it (`"derived"`). */
    readonly columnCount: SheetViewModel.ColumnCount,
    deps: SheetViewModel.Deps<Binder>,
    readonly anchorDate: NaiveDate,
    /** The sheet this view renders. */
    readonly handle: SheetHandle,
  ) {
    this.#deps = deps;
  }

  /** Whether the viewer may edit this sheet (not a `viewer` role). */
  get editable(): boolean {
    return this.handle.editable;
  }

  /** The sheet's reserved lookup key (`:calendar`, …), or `null`. */
  get lookupKey(): Option<string> {
    return this.handle.key.lookupKey ?? null;
  }

  /** `"local"` for an unsaved (ephemeral) sheet, `"synced"` once server-backed. */
  get persistence(): "local" | "synced" {
    return this.handle.persistence;
  }

  /** The invalidation sink this sheet publishes into. */
  get invalidation(): InvalidationSink {
    return this.handle.invalidation;
  }

  /** This sheet's cells (see `SheetHandle.Cells`). */
  get cells(): SheetHandle.Cells {
    return this.handle.cells;
  }

  /**
   * Mirror a formula already persisted rows-side (a row option's
   * `variant.apply`) into this sheet's lona-js row, so the joined tree reads
   * it too. Nothing is sent. `false` when row `id` has no lona-js entry — a
   * rows-side-only expression row, which has nothing to mirror into.
   */
  mirrorFormula(id: RowLocalId, formula: string): boolean {
    return mirrorFormulaInto(this.handle, id, formula);
  }

  /**
   * Re-derive what the tree's structure feeds (the label / lookup-key maps
   * and the formula dependency graph) after a structural change made
   * elsewhere — a rows-side re-key the joined tree has not seen. Emits
   * `SheetStructureChanged`; nothing is sent.
   */
  invalidateStructure(): void {
    platformOf(this.handle).local.invalidateStructure();
  }

  /**
   * Adopt a row the server created through the host's own request: register
   * it with the client, list it on this sheet, mirror it rows-side and insert
   * it at the top level, announced as `actor`. Nothing is sent — the create
   * already happened; `handle.rows.add` is the persisting verb.
   */
  adoptCreated(created: Network.CreateRowResponse, actor: Actor): SheetRow.Handle {
    return platformOf(this.handle).local.adoptCreated(created, actor);
  }

  /** The rows-side row for `id`, or `null` when the rows-side sheet lacks it. */
  peekRow(id: RowLocalId): Option<ModelRowReads> {
    return this.handle.model.peekRow(id) ?? null;
  }

  /**
   * The rows-side sheet when it holds row `id` — the copy-on-write target for
   * a renderer display option — else `null` (a row not hydrated rows-side
   * falls back to the legacy `row.update`).
   */
  modelHolding(id: RowLocalId): Option<SheetModel> {
    const rowsSheet = this.handle.model;
    return rowsSheet.peekRow(id) != null ? rowsSheet : null;
  }

  get isTimezoneLocked(): boolean {
    return (
      this.viewConfig.timescale === "day" &&
      this.viewConfig.tzType === "time-traveler"
    );
  }

  #joinedTreeCache: Option<SheetRowTree> = null;
  #treeGeneration: number = -1;

  /** The rendered sheet's id (`handle.id`). */
  get id(): SheetId {
    return this.handle.id;
  }
  /** The client's row-identity scope, resolve-only (`client.ids`). */
  get ids(): RowLocalId.Resolver {
    return this.#deps.sdk.ids;
  }
  get joinedTree(): SheetRowTree {
    if (!this.#joinedTreeCache || this.#treeGeneration !== this.handle.generation) {
      // Relocation-repaint diagnostics (session breadcrumb): the cached
      // joined tree is rebuilt because the handle generation moved
      // (rows-side structural change — see `handle/structural-bump`).
      Tracing.log("viewmodel/tree-rebuild", {
        from: this.#treeGeneration,
        to: this.handle.generation,
      });
      this.#joinedTreeCache = this.handle.joinedTree({
        timescale: this.viewConfig.timescale,
        host: this.#deps.sdk.host,
        invalidation: this.#deps.sdk.invalidation,
      });
      this.#treeGeneration = this.handle.generation;
    }
    return this.#joinedTreeCache;
  }
  get timescale(): Timescale {
    return this.viewConfig.timescale;
  }
  get layout(): SheetLayoutSpec<RowLocalId> {
    return this.joinedTree.layout;
  }
  invalidateLayout(): void {
    this.joinedTree.invalidateLayout();
  }

  /** The row metadata this sheet renders `id` from, as its frozen reads face (`handle.sheetRow`). */
  sheetRow(id: RowLocalId): Option<SheetRowReads> {
    return this.handle.sheetRow(id);
  }


  static async make<Binder>(
    input: Network.UserSheetWithRows | SheetHandle,
    deps: SheetViewModel.Deps<Binder>,
    anchorDate: NaiveDate,
    initialViewConfig: Option<SheetViewModel.InitialViewConfig>,
  ): Promise<SheetViewModel<Binder>> {
    const handle =
      input instanceof SheetHandle
        ? input
        : deps.sdk.parseSheet(input, {
          ...SheetHandle.ParseConfig.DEFAULT,
          debugRows: deps.debugRows,
        });

    try {
      const sheet = handle.network;
      const initial = initialViewConfig ?? deps.sdk.preferences
        .sheetView(SheetId.of(sheet.id))
        .peekResolved(sheet.timeScale ?? "week", sheet.lookupKey ?? null);
      const viewConfig = initial.config;
      Tracing.dev("sheet-view-model/make/vc", viewConfig);

      // Rebuild system rows with debug/ephemeral flags from deps.
      // SheetHandle.parse may have been called without these flags
      // (e.g., sdk.open(key) doesn't know about debug preferences).
      sheetChromeOf(handle).rebuildSystemRows({
        debugRows: deps.debugRows,
        emit: "nothing",
        timescale: viewConfig.timescale,
        calendarSystems: deps.sdk.preferences.calendar().peek().secondaryCalendars,
      });

      // Sync Sheet.timescale with the active view config so alias
      // templates expand against the right branch on first hydration.
      // No-op if already in sync. Paired with the equivalent call in
      // `clone.withViewConfig` for subsequent view-config switches.
      sheetChromeOf(handle).setTimescale(viewConfig.timescale);

      const columnCount: SheetViewModel.ColumnCount = initial.countIsExplicit ? "explicit" : "derived";
      const renderer = await deps.renderer(
        anchorDate,
        viewConfig,
        handle.preferredColumnWidthPx,
        columnCount,
      );

      return new SheetViewModel(
        renderer,
        viewConfig,
        columnCount,
        deps,
        anchorDate,
        handle,
      );
    } catch (error) {
      // Wire input acquired this lease; an existing handle remains caller-owned.
      if (!(input instanceof SheetHandle)) {
        try { handle.dispose(); } catch { /* Preserve the preparation failure. */ }
      }
      throw error;
    }
  }

  readonly clone: SheetViewModel.Cloner<Binder> = ((self: SheetViewModel<Binder>) => ({
    withItself(): Promise<SheetViewModel<Binder>> {
      return this.withViewConfig(null);
    },

    withNetwork(n: Network.UserSheetWithRows): Promise<SheetViewModel<Binder>> {
      return SheetViewModel.make(n, self.#deps, self.anchorDate, null);
    },

    withViewConfig: async (
      viewConfig: Option<SheetViewConfig>,
    ): Promise<SheetViewModel<Binder>> => {
      const resolvedViewConfig = viewConfig ?? self.viewConfig;
      const columnCount: SheetViewModel.ColumnCount = viewConfig == null
        ? self.columnCount
        : "explicit";
      // Same factory the handle was opened with — a view-config switch changes
      // the columns, never which chrome the sheet has. Hardcoding DEFAULT here
      // meant a PREVIEW handle grew a footer back on its first range change.
      const factory = self.handle.systemRowFactory ?? SystemRow.Factory.DEFAULT;
      const debugRows = self.#deps.debugRows;
      const systemRowsResult = SystemRows.build({
        debugRows,
        mode: self.handle.persistence === "synced"
          ? "persisted"
          : self.handle.isTemplate ? "template" : "ephemeral",
        catalog: isBuiltinSheetKey(self.handle.key.lookupKey) ? "builtin" : "user",
      }, factory);

      // Adjust for existing tree (adds timeline rows, checks second header)
      const existingRows = self.joinedTree.flattened.nonSystemRows;
      factory.onRowsBuilt(
        systemRowsResult,
        existingRows,
        resolvedViewConfig.timescale,
        { calendarSystems: self.#deps.sdk.preferences.calendar().peek().secondaryCalendars },
      );

      // 1) Replace system rows via handle
      const ids = platformOf(self.#deps.sdk).ids;
      const footerLocalIds = SheetHandle.collectFooterRowIds(systemRowsResult.rows, ids);
      const defs: SheetHandle.SystemRowPlacement[] = systemRowsResult.rows.map(
        (srow) => {
          const localId = ids.resolve(RowKey.of(srow.id.toString()));
          platformOf(self.#deps.sdk).registerRow(localId, srow);
          const def: SheetHandle.SystemRowPlacement = {
            id: localId,
            position: srow.position,
            isFooter: footerLocalIds.has(localId),
            children: srow.children?.length
              ? srow.children.map((id) => ids.resolve(RowKey.of(id.toString())))
              : null,
          };
          return def;
        },
      );
      const chrome = sheetChromeOf(self.handle);
      chrome.replaceSystemRows(defs);
      // Rows-side half of the SAME swap — mirrored through the handle so the
      // structural subscription doesn't re-bump `generation` once per
      // re-inserted system row (see `SheetHandle.mirrorSystemRowsToRowsSheet`).
      chrome.mirrorSystemRowsToRowsSheet(systemRowsResult.rows);

      // Sync Sheet.timescale with the new view config so alias templates
      // (e.g. the `calendar` template that branches `timeline[column]` ↔
      // `grid` on `ctx.timeScale === "month"`) re-expand with the correct
      // branch. Without this, switching week → month leaves the Sheet
      // stuck on the old timeScale and the month grid never synthesizes.
      sheetChromeOf(self.handle).setTimescale(resolvedViewConfig.timescale);

      // 2) Renderer — keep the anchor the user is looking at. A view-config
      // switch (timescale, column count, the personal-timezones toggle) and
      // the `withItself` refresh path used to rebuild from `NaiveDate.today()`,
      // silently jumping the visible window back to today; the viewed date is
      // orthogonal to the view config, so it survives the switch. Explicit
      // re-anchoring stays `withAnchorDate` / `goToDate`.
      const anchorDate = self.anchorDate;
      const renderer = await self.#deps.renderer(
        anchorDate,
        resolvedViewConfig,
        self.handle.preferredColumnWidthPx,
        columnCount,
      );

      return new SheetViewModel(
        renderer,
        resolvedViewConfig,
        columnCount,
        self.#deps,
        anchorDate,
        self.handle,
      );
    },

    /**
     * Re-anchor the calendar window to `anchorDate` without changing the
     * view config. The tree/system rows are timeScale-driven and unchanged,
     * so only the renderer's column window is re-derived. (`withViewConfig`
     * preserves the current anchor, so ordering against view-config changes
     * no longer matters.)
     */
    withAnchorDate: async (
      anchorDate: NaiveDate,
    ): Promise<SheetViewModel<Binder>> => {
      const renderer = await self.#deps.renderer(
        anchorDate,
        self.viewConfig,
        self.handle.preferredColumnWidthPx,
        self.columnCount,
      );
      return new SheetViewModel(
        renderer,
        self.viewConfig,
        self.columnCount,
        self.#deps,
        anchorDate,
        self.handle,
      );
    },
  }))(this);
}

/**
 * Types and values that go with `SheetViewModel`: `ColumnCount`, `Cloner`,
 * `InitialViewConfig`, `Renderer`, `Deps`, `Surface`, and 2 more.
 */
export namespace SheetViewModel {
  /**
   * Whether a view's column count is `"explicit"` (its view config states
   * it: the renderer holds it) or `"derived"` (the sheet's width decides it:
   * a weak constraint).
   */
  export type ColumnCount = "explicit" | "derived";

  /** `SheetViewModel.clone`: a new view model over the same handle, one input changed. */
  export interface Cloner<Binder> {
    /** The same view config and anchor, re-rendered. */
    withItself(): Promise<SheetViewModel<Binder>>;
    /** A view model over the wire sheet `n`, with this one's deps and anchor. */
    withNetwork(n: Network.UserSheetWithRows): Promise<SheetViewModel<Binder>>;
    /** `viewConfig` (`null`: this one's) applied, the anchor kept. */
    withViewConfig(viewConfig: Option<SheetViewConfig>): Promise<SheetViewModel<Binder>>;
    /** The column window re-anchored on `anchorDate`, the view config kept. */
    withAnchorDate(anchorDate: NaiveDate): Promise<SheetViewModel<Binder>>;
  }

  export interface InitialViewConfig {
    readonly config: SheetViewConfig;
    readonly countIsExplicit: boolean;
  }

  /**
   * A view's column renderer — the structural type the sheets-ui
   * `SheetIndexRenderer` satisfies with `Binder` its `SheetBinder`; a
   * headless renderer (`TestRenderers.headless`, the CLI) binds nothing and
   * is a `Renderer<never>`.
   */
  export type Renderer<Binder> = {
    readonly mapper: ColumnIndex.Mapper;
    readonly initialWidth: SheetLayoutWidth;
    readonly initialConstraints: SheetLayoutWidth.Constraints;
    toWidth(key: ColumnIndex.Key.Resolved): Option<number>;
    widthForIndex(index: number): Option<number>;
    /** The binder of column `key`, or `null` to use the view's default one. */
    toBinder(key: ColumnIndex.Key.Resolved): Option<Binder>;
  };

  export type Deps<Binder> = {
    sdk: LonaSdk.Client;
    renderer: (
      anchorDate: NaiveDate,
      viewConfig: SheetViewConfig,
      preferredColumnWidthPx: Option<number>,
      columnCount: ColumnCount,
    ) => Promise<Renderer<Binder>>;
    /** The debug system rows the sheet builds (`[]`: none). */
    debugRows: readonly SheetHandle.DebugRow[];
  };

  /**
   * What a view reads — the sheets-ui `Sheet` element, its plugins and the
   * app's row options: the tree and layout, the view config, row and cell
   * reads, and the two local-only verbs a row option needs (`mirrorFormula`,
   * `invalidateStructure`). `handle` is the sheet it renders; a renderer host
   * reaches that handle's seam as `platformOf(view.handle)` through
   * `@tento/lona/host`, never through the view.
   */
  export interface Surface<Binder> {
    /** The sheet this view renders. */
    readonly handle: SheetHandle;
    readonly joinedTree: SheetRowTree;
    readonly ids: RowLocalId.Resolver;
    readonly indexRenderer: Renderer<Binder>;
    /** The rendered sheet's id (`handle.id`). */
    readonly id: SheetId;
    readonly timescale: Timescale;
    readonly viewConfig: SheetViewConfig;
    /** The day the column window is anchored on — the one `goToDate` moves and
     *  a view-config flip carries forward unchanged. Compare THIS to ask "did
     *  the anchor move", never the mapper's `startDate`: that is the anchor
     *  aligned to the mapper's period (a week mapper starts on the Monday), so
     *  it moves on every timescale flip whose anchor is not period-aligned. */
    readonly anchorDate: NaiveDate;
    readonly isTimezoneLocked: boolean;
    readonly layout: SheetLayoutSpec<RowLocalId>;
    invalidateLayout(): void;
    /** Whether the viewer may edit this sheet. */
    readonly editable: boolean;
    /** The sheet's reserved lookup key, or `null`. */
    readonly lookupKey: Option<string>;
    /** `"local"` (unsaved) or `"synced"`. */
    readonly persistence: "local" | "synced";
    /** The invalidation sink this sheet publishes into. */
    readonly invalidation: InvalidationSink;
    /** This sheet's cells. */
    readonly cells: SheetHandle.Cells;
    /** The row metadata this sheet renders `id` from, as its reads face, or `null`. */
    sheetRow(id: RowLocalId): Option<SheetRowReads>;
    /** The rows-side row for `id`, or `null`. */
    peekRow(id: RowLocalId): Option<ModelRowReads>;
    /** The rows-side sheet when it holds row `id`, else `null`. */
    modelHolding(id: RowLocalId): Option<SheetModel>;
    /** See `SheetViewModel.mirrorFormula`. */
    mirrorFormula(id: RowLocalId, formula: string): boolean;
    /** See `SheetViewModel.invalidateStructure`. */
    invalidateStructure(): void;
  }

  /**
   * The host-built parts of a `Readable` a host builds without a
   * `SheetViewModel` — the data only the host knows (see `surfaceFor`).
   * Plain data, not a UI parts.
   */
  export interface HostParts<Binder> {
    /**
     * The joined tree, read on every access: the host re-resolves it when
     * `handle.generation` moves, as `SheetViewModel.joinedTree` does.
     */
    joinedTree(): SheetRowTree;
    readonly ids: RowLocalId.Resolver;
    readonly indexRenderer: Renderer<Binder>;
    /** The rendered sheet's id (`handle.id`). */
    readonly id: SheetId;
    readonly viewConfig: SheetViewConfig;
    readonly anchorDate: NaiveDate;
    readonly isTimezoneLocked: boolean;
  }

  /**
   * A `Readable` over `handle` and the host-built `parts`, for a surface that
   * renders a handle with no `SheetViewModel` (the template preview). The
   * handle-derived reads (`editable`, `persistence`, `cells`, `peekRow`, …)
   * stay live; `timeScale` and `layout` follow the parts' current tree.
   */
  export function surfaceFor<Binder>(handle: SheetHandle, parts: HostParts<Binder>): Surface<Binder> {
    return {
      handle,
      get joinedTree(): SheetRowTree {
        return parts.joinedTree();
      },
      ids: parts.ids,
      indexRenderer: parts.indexRenderer,
      id: parts.id,
      get timescale(): Timescale {
        return parts.joinedTree().timescale;
      },
      viewConfig: parts.viewConfig,
      anchorDate: parts.anchorDate,
      isTimezoneLocked: parts.isTimezoneLocked,
      get layout(): SheetLayoutSpec<RowLocalId> {
        return parts.joinedTree().layout;
      },
      invalidateLayout(): void {
        parts.joinedTree().invalidateLayout();
      },
      get editable(): boolean {
        return handle.editable;
      },
      get lookupKey(): Option<string> {
        return handle.key.lookupKey ?? null;
      },
      get persistence(): "local" | "synced" {
        return handle.persistence;
      },
      get invalidation(): InvalidationSink {
        return handle.invalidation;
      },
      get cells(): SheetHandle.Cells {
        return handle.cells;
      },
      sheetRow: (id) => handle.sheetRow(id),
      peekRow: (id) => handle.model.peekRow(id) ?? null,
      modelHolding: (id) =>
        handle.model.peekRow(id) != null ? handle.model : null,
      mirrorFormula: (id, formula) => mirrorFormulaInto(handle, id, formula),
      invalidateStructure: () => platformOf(handle).local.invalidateStructure(),
    };
  }
}

/** `SheetViewModel.mirrorFormula`, shared with `surfaceFor`. */
function mirrorFormulaInto(handle: SheetHandle, id: RowLocalId, formula: string): boolean {
  if (handle.sheetRow(id) == null) return false;
  platformOf(handle).local.update(id, { formula }, SheetHandle.ACTOR);
  return true;
}
